
import RUMOneLogger, { IBreakdown, IBreakdownValue, LoggingFuncType } from '@ms/odsp-utilities/lib/logging/rumone/RUMOneLogger';
import ControlPerformanceData from '@ms/odsp-utilities/lib/logging/rumone/ControlPerformanceData';
import PageTransitionType from '@ms/odsp-utilities/lib/logging/rumone/PageTransitionType';
import { itemsViewPerfMarkers } from './PerfMarkers';
import { IMeasurementInfo } from '@ms/items-view/lib/private/facets/LoggerFacets';
import { spFolderItemSetKeyFacet, spItemSetKeyFacet } from '../../../dataSource/Keys';
import { IItemCacheThunkAction, IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { RUMOneDataUpload } from '@ms/odsp-utilities/lib/logging/events/RUMOneDataUpload.event';
import { getTelemetryManager } from '@ms/items-view/lib/private/operations/telemetry/getTelemetryManager';
import { asOnceOnly } from '@ms/items-view/lib/private/store/middleware/RunOnceMiddleware';
import * as PerformanceMarker from '@ms/odsp-utilities/lib/performance/PerformanceMarker';
import { OnePageNavigationStartMarkerName } from '@ms/odsp-utilities/lib/performance/PerformanceCollection';

function getRUMOneLoggingFunc(): IItemCacheThunkAction<LoggingFuncType> {
    return (dispatch: IItemCacheDispatch) => {
        const manager = dispatch(getTelemetryManager);
        const RUMOneDataUploadWithManager = RUMOneDataUpload.withManager(manager);
        return (streamName: string, dictProperties: {}) => {
            RUMOneDataUploadWithManager.logData({ streamName: streamName, dictionary: dictProperties });
        };
    };
}

function getRUMOneLogger(): IItemCacheThunkAction<RUMOneLogger> {
    return asOnceOnly((dispatch: IItemCacheDispatch) => {
        const rumone: RUMOneLogger = RUMOneLogger.getRUMOneLogger();
        const loggingFunc = dispatch(getRUMOneLoggingFunc());
        rumone.registerLoggingFunc(loggingFunc);
        return rumone;
    });
}

export function resetRUMOne(measurementInfo?: IMeasurementInfo): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        const rumone = dispatch(getRUMOneLogger());

        if (measurementInfo && measurementInfo.keyChanges &&
            measurementInfo.keyChanges.currentItemKey !== measurementInfo.keyChanges.previousItemKey) {
            PerformanceMarker.mark(itemsViewPerfMarkers.render.start);
        }

        if (!rumone.isRunning()) {
            const pageTransition = getPageTransitionType(rumone, measurementInfo);

            if (!measurementInfo || pageTransition !== PageTransitionType.none) {
                rumone.resetLogger();
            }
        }
    };
}

export function logRUMOneForItemsView(
    renderEndTime: number,
    scenarioId?: string,
    measurementInfo?: IMeasurementInfo): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        if (measurementInfo && measurementInfo.keyChanges &&
            measurementInfo.keyChanges.currentItemKey !== measurementInfo.keyChanges.previousItemKey) {
            PerformanceMarker.mark(itemsViewPerfMarkers.render.end);

            PerformanceMarker.measure(`Items-View.Render`, itemsViewPerfMarkers.render.start, itemsViewPerfMarkers.render.end);
        }

        const rumone = dispatch(getRUMOneLogger());
        const {
            itemsScopeItemKey = '',
            itemsScopeInitTime = 0
        } = measurementInfo || {};
        const controlName = `ItemsViewListView${itemsScopeItemKey}`;
        rumone.writeScenarioId(`${scenarioId || 'Unknown'}.ItemsViewListView`);
        rumone.addExpectedControl(controlName);
        rumone.collectW3CPerfTimings();
        let transitionType = getPageTransitionType(rumone, measurementInfo);
        rumone.writePageTransitionType(transitionType);
        const transactionStartTime = rumone.getMarkerTime(itemsViewPerfMarkers.itemsView.start) ||
            PerformanceMarker.getMarkerTime('OnePageNavigation.Start') ||
            itemsScopeInitTime;
        const authTokenStart = rumone.getMarkerTime(itemsViewPerfMarkers.authToken.start);
        const authTokenEnd = rumone.getMarkerTime(itemsViewPerfMarkers.authToken.end);
        const dataFetchStart = rumone.getMarkerTime(itemsViewPerfMarkers.dataFetch.start);
        const dataFetchEnd = rumone.getMarkerTime(itemsViewPerfMarkers.dataFetch.end);
        const appStartMarkTime = rumone.getMarkerTime(itemsViewPerfMarkers.appStart.start);
        let perfDataBreakDown: IBreakdown = {
            'itemsView.appStart': {
                data: appStartMarkTime - transactionStartTime > 0 ? appStartMarkTime - transactionStartTime : 0,
                needFlatten: true
            }
        };
        if (authTokenStart && authTokenEnd) {
            perfDataBreakDown = {
                ...perfDataBreakDown,
                'itemsView.authTokenDuration': {
                    data: authTokenEnd - authTokenStart,
                    needFlatten: true
                }
            };
        }
        let dataFetch = 0;
        if (dataFetchStart && dataFetchEnd) {
            dataFetch = dataFetchEnd - dataFetchStart;
            perfDataBreakDown = {
                ...perfDataBreakDown,
                'itemsView.dataFetch': {
                    data: dataFetch,
                    needFlatten: true
                }
            };
        }
        perfDataBreakDown = {
            ...perfDataBreakDown,
            'itemsView.render': {
                data: renderEndTime - Math.max(itemsScopeInitTime, isNaN(appStartMarkTime) ?
                    0 : appStartMarkTime, transactionStartTime) - dataFetch,
                needFlatten: true
            }
        };

        const controlPerformanceData = new ControlPerformanceData(
            controlName,                                     // control name
            transactionStartTime,                            // start time
            renderEndTime,                                   // end time
            computeRenderTime,                               // callback to calculate control render time
            () => { return typeof transactionStartTime !== 'undefined'; });
        rumone.writeControlPerformanceData(controlPerformanceData);
        rumone.writeEUPLBreakdown(JSON.stringify(perfDataBreakDown));
    };
}

export function addEUPLBreakdown(name: string, value: number | IBreakdownValue, overwrite?: boolean): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        const rumone = dispatch(getRUMOneLogger());
        rumone.addEUPLBreakdown(name, value, overwrite);
    };
}

function computeRenderTime(rumone: RUMOneLogger, controlData: ControlPerformanceData): number {
    if (rumone && controlData) {
        return controlData.endTime - controlData.startTime;
    }
    return 0;
}

function getPageTransitionType(rumone: RUMOneLogger, measurementInfo: IMeasurementInfo | undefined): PageTransitionType {
    if (measurementInfo) {
        if (measurementInfo.isFullPageLoad) {
            if (rumone.getMarkerTime(OnePageNavigationStartMarkerName)) {
                return PageTransitionType.onePageAppNavigation;
            } else {
                return PageTransitionType.fullPageLoad;
            }
        }
        let previousFolderItemSetKey, previousItemSetKey, currentKey;
        const {
            previousItemKey = undefined,
            currentItemKey = undefined
        } = measurementInfo.keyChanges || {};
        if (previousItemKey && spFolderItemSetKeyFacet.matches(previousItemKey)) {
            previousFolderItemSetKey = spFolderItemSetKeyFacet.deserialize(previousItemKey);
        }
        if (previousItemKey && spItemSetKeyFacet.matches(previousItemKey)) {
            previousItemSetKey = spItemSetKeyFacet.deserialize(previousItemKey);
        }
        if (currentItemKey && spFolderItemSetKeyFacet.matches(currentItemKey)) {
            currentKey = spFolderItemSetKeyFacet.deserialize(currentItemKey);
        }
        if (!previousFolderItemSetKey && !previousItemSetKey) {
            // no previous key, it is either full page load or switch to a different list
            return PageTransitionType.onePageAppNavigation;
        }
        if (currentKey) {
            const previousListFullUrl = previousFolderItemSetKey && previousFolderItemSetKey.listFullUrl ||
                previousItemSetKey && previousItemSetKey.parentItemKey && previousItemSetKey.parentItemKey.listFullUrl;
            const previousRootFolder = previousFolderItemSetKey && previousFolderItemSetKey.rootFolder ||
                previousItemSetKey && previousItemSetKey.parentItemKey && previousItemSetKey.parentItemKey.rootFolder;

            if (previousListFullUrl && previousListFullUrl !== currentKey.listFullUrl) {
                return PageTransitionType.onePageAppNavigation;
            } else if (previousRootFolder !== currentKey.rootFolder) {
                return PageTransitionType.none;
            }
        }
    }
    return PageTransitionType.none;
}
